/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Crop Transform Node
   GPU-accelerated rectangular clipping and canvas bounding.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';
import { getMediaDimensions } from '../../rendering/media';

const CROP_FRAGMENT_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform vec4 u_cropRect; // x, y, width, height in normalized [0, 1]

void main() {
  vec2 uv = u_cropRect.xy + v_uv * u_cropRect.zw;
  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    fragColor = vec4(0.0);
    return;
  }
  fragColor = texture(u_image, uv);
}
`;

export const cropNode: NodeDefinition = {
  type: 'transform.crop',
  category: 'Transform',
  label: 'Crop',
  description: 'Clips image to a specific sub-rectangle and adjusts canvas bounds',
  keywords: ['crop', 'cut', 'clip', 'trim', 'bounds'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Cropped', type: 'image' },
  ],
  params: [
    {
      id: 'x',
      name: 'Left Offset',
      type: 'slider',
      defaultValue: 0,
      min: 0,
      max: 100,
      step: 0.5,
      unit: '%',
    },
    {
      id: 'y',
      name: 'Top Offset',
      type: 'slider',
      defaultValue: 0,
      min: 0,
      max: 100,
      step: 0.5,
      unit: '%',
    },
    {
      id: 'width',
      name: 'Width',
      type: 'slider',
      defaultValue: 100,
      min: 1,
      max: 100,
      step: 0.5,
      unit: '%',
    },
    {
      id: 'height',
      name: 'Height',
      type: 'slider',
      defaultValue: 100,
      min: 1,
      max: 100,
      step: 0.5,
      unit: '%',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: CROP_FRAGMENT_SHADER,
    calculateDimensions: (inputs, params) => {
      const srcDim = getMediaDimensions(inputs.image);
      const wPercent = Math.max(1, Math.min(100, Number(params.width) || 100)) / 100;
      const hPercent = Math.max(1, Math.min(100, Number(params.height) || 100)) / 100;
      return {
        width: Math.max(1, Math.round(srcDim.width * wPercent)),
        height: Math.max(1, Math.round(srcDim.height * hPercent)),
      };
    },
    getUniforms: (inputs, params) => {
      const x = Math.max(0, Math.min(100, Number(params.x) || 0)) / 100;
      const y = Math.max(0, Math.min(100, Number(params.y) || 0)) / 100;
      const w = Math.max(1, Math.min(100, Number(params.width) || 100)) / 100;
      const h = Math.max(1, Math.min(100, Number(params.height) || 100)) / 100;

      return {
        u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
        u_cropRect: [x, y, w, h],
      };
    },
  }),
};
